import _extends from "@swc/helpers/src/_extends.mjs";
import _object_without_properties from "@swc/helpers/src/_object_without_properties.mjs";
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import c from 'classnames';
import ReactModal from 'react-modal';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ThemeOverride, ThemeOptions } from '@wix/da-react-context/pkg/ThemeContext';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useVisualViewport } from '../../../hooks/useVisualViewport';
import IconButton from '../../Buttons/IconButton';
import Close from '../../Icons/24x24/Close';
import { PopupContext } from '../../popups/PopupContext';
import s from './ModalWrapper.scss';
/**
 * The root component of any modal. This is a wrapper for `react-popper`'s `<ReactPopper />` component.
 *
 * Provides theming, body scroll locking, and some additional styling.
 */ const ModalWrapper = (_param)=>{
    var { className , matchTheme =false , closeButtonOutside =false , useTransparentOverlay =false , hasCloseButton =true , width , height , useFixedMobileHeight , isFullscreen , bodyScrollLockOptions , onRequestClose , children , innerContentRef  } = _param, rest = _object_without_properties(_param, [
        "className",
        "matchTheme",
        "closeButtonOutside",
        "useTransparentOverlay",
        "hasCloseButton",
        "width",
        "height",
        "useFixedMobileHeight",
        "isFullscreen",
        "bodyScrollLockOptions",
        "onRequestClose",
        "children",
        "innerContentRef"
    ]);
    const isMobile = useContext(MobileContext);
    // Mobile fixed to viewport fixes
    const [visualViewport, setVisualViewport] = useState();
    const viewportHandler = useCallback((v)=>{
        setVisualViewport(v);
    }, []);
    useVisualViewport(viewportHandler, isMobile && !!useFixedMobileHeight);
    const style = {
        content: {
            width,
            height
        }
    };
    const contentStyle = {};
    // Apply fixed height for mobile if requested, helper for fixing Safari being lame
    if (isMobile && visualViewport && !height) {
        style.content.height = visualViewport.height;
        style.content.transform = `translate(${visualViewport.offsetLeft}px,${visualViewport.offsetTop}px)`;
        style.content.scale = visualViewport.scale;
        // Calculate positions from top left
        style.content.top = 0;
        if (isFullscreen) {
            style.content.left = 0;
        }
        style.content.position = 'fixed';
        // Set the max height of the content, fixes content expanding beyond viewport
        contentStyle.maxHeight = visualViewport.height;
    }
    // Mobile body scroll locking - Important makes IOS scroll
    const contentRef = useRef(null);
    const { onLock , onUnlock , setScrollableRef , scrollableElementRef  } = useBodyScrollLock(bodyScrollLockOptions);
    useEffect(()=>{
        const focusedEl = document.activeElement;
        const contentEl = contentRef.current;
        const isFocused = (el)=>el === focusedEl || el.contains(focusedEl);
        const needsFocus = contentEl && (!focusedEl || !isFocused(contentEl));
        let focusTimeout;
        if (needsFocus) {
            // useEffect is supposed to be invoked when everything mounted, but without this timeout something else steals the focus
            focusTimeout = setTimeout(()=>contentEl.focus(), 100);
        }
        return ()=>focusTimeout && clearTimeout(focusTimeout);
    }, []);
    const setContentRef = (instance)=>{
        contentRef.current = instance;
        // Important makes IOS scroll
        // It might happen that the Popup renders a component that calls setScrollableRef on its own, such as ScrollableContent.
        // In this case we avoid updating the ref because that would cause an incorrect re-lock to the modal content element.
        // This is actually the fallback, in general you have ModalLayout which will set the default scrollale below.
        if (!scrollableElementRef.current) {
            setScrollableRef(instance);
            innerContentRef && innerContentRef(instance);
        }
    };
    const setDefaultScrollRef = (instance)=>{
        // Important makes IOS scroll
        // It might happen that the Popup renders a component that calls setScrollableRef on its own, such as ScrollableContent.
        // In this case we avoid updating the ref because that would cause an incorrect re-lock to the modal content element.
        // This is the default when using ModalLayout, there is a fallback above which is the the ReactModal content.
        if (!scrollableElementRef.current) {
            setScrollableRef(instance);
            innerContentRef && innerContentRef(instance);
        }
    };
    const handleClose = useCallback((e)=>{
        onUnlock();
        onRequestClose && onRequestClose(e);
    }, [
        onRequestClose,
        onUnlock
    ]);
    const closeButton = hasCloseButton ? /*#__PURE__*/ React.createElement(IconButton, {
        className: c(closeButtonOutside && s['close-button-outside'], s['close-button']),
        size: "large",
        icon: Close,
        onClick: handleClose
    }) : null;
    return /*#__PURE__*/ React.createElement(ReactModal, _extends({
        className: c(s['root'], closeButtonOutside && s['overflow'], isFullscreen && s['fullscreen'], className),
        overlayClassName: c(s['overlay'], useTransparentOverlay && s['transparent-overlay']),
        portalClassName: 'ReactModalPortal',
        style: style,
        contentRef: setContentRef,
        onAfterOpen: onLock,
        onRequestClose: handleClose
    }, rest), /*#__PURE__*/ React.createElement(ThemeOverride, {
        theme: matchTheme ? 'match' : ThemeOptions.LIGHT,
        className: s['theme-wrapper']
    }, /*#__PURE__*/ React.createElement(PopupContext.Provider, {
        value: {
            isInPopup: true,
            setScrollableContentRef: setScrollableRef,
            setDefaultScrollableRef: setDefaultScrollRef
        }
    }, children), closeButtonOutside ? /*#__PURE__*/ React.createElement(ThemeOverride, {
        theme: ThemeOptions.DARK
    }, closeButton) : closeButton));
};
ModalWrapper.displayName = 'ModalWrapper';
export default ModalWrapper;
